/**
 * Day-series bucketing for the shared viz layer. Pure functions, no i18n, no api.
 */

/**
 * Weekly buckets past 30 points, so a 90-day window is readable.
 * A bucket with no measured day stays `null` — the line must BREAK there, and an
 * average over "the days that happened to run" is not a zero (§9.5 rule 2).
 */
export function bucketK(len: number): number {
  return len > 30 ? 7 : 1;
}

export function bucketize(
  series: { d: string; presence: number | null; n: number }[],
): { label: string; presence: number | null; n: number }[] {
  const k = bucketK(series.length);
  const out: { label: string; presence: number | null; n: number }[] = [];
  for (let i = 0; i < series.length; i += k) {
    const chunk = series.slice(i, i + k);
    const vals = chunk.map((p) => p.presence).filter((v): v is number => v != null);
    out.push({
      label: chunk[0].d,
      presence: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
      n: chunk.reduce((s, p) => s + (p.n ?? 0), 0),
    });
  }
  return out;
}

/**
 * Same buckets as `bucketize`, for a series that shares its day axis. `k` is passed
 * in rather than recomputed so an intent line can never land on a different bucket
 * width than the average it is drawn against.
 */
export function bucketVals(points: (number | null)[], k: number): (number | null)[] {
  const out: (number | null)[] = [];
  for (let i = 0; i < points.length; i += k) {
    const vals = points.slice(i, i + k).filter((v): v is number => v != null);
    out.push(vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Calendar grouping (2026-10-08)
// ---------------------------------------------------------------------------

export type CalendarGrain = 'day' | 'week' | 'month';

/** ISO week start (Monday) of a `YYYY-MM-DD` key, as a `YYYY-MM-DD` key. */
function mondayOf(day: string): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  // getUTCDay: 0 = Sunday, so Sunday is 6 days after its Monday.
  const back = (d.getUTCDay() + 6) % 7;
  return new Date(d.getTime() - back * 86_400_000).toISOString().slice(0, 10);
}

const keyOf = (day: string, grain: CalendarGrain): string =>
  grain === 'month' ? day.slice(0, 7) : grain === 'week' ? mondayOf(day) : day;

export interface CalendarGroup {
  /** `YYYY-MM-DD` (day / week start) or `YYYY-MM` (month). */
  key: string;
  /** Indices into the input array that fold into this group, in order. */
  idx: number[];
}

/**
 * Group a series' bucket keys by the COARSEST CALENDAR grain that fits in `max`
 * columns, trying day → week → month.
 *
 * Why calendar and not `bucketK`'s "every k-th measured point": the series carries
 * only MEASURED buckets, so a count-based fold puts 28 juil. and 29 sept. in
 * adjacent equal-width columns and the axis then asserts they are adjacent in time.
 * Grouping by calendar key makes a column mean a real span — and an unmeasured
 * month produces NO column rather than a zero one, which is the same rule as a
 * broken line (§9.5: absent ≠ zero).
 *
 * `month` is the last resort and is never exceeded: two years is 24 columns, which
 * is the one case where `max` is deliberately overshot rather than folding to
 * quarters — a quarter is not a unit anyone measures in here.
 *
 * A monthly-tier series (keys already `YYYY-MM`) is returned as-is at `'month'`:
 * `keyOf` is idempotent on it and no second fold is possible.
 */
export function calendarGroups(
  keys: string[],
  max = 10,
): { grain: CalendarGrain; groups: CalendarGroup[] } {
  const monthly = keys.every((k) => k.length === 7);
  const grains: CalendarGrain[] = monthly ? ['month'] : ['day', 'week', 'month'];
  let last: { grain: CalendarGrain; groups: CalendarGroup[] } | null = null;
  for (const grain of grains) {
    const byKey = new Map<string, number[]>();
    keys.forEach((k, i) => {
      const g = keyOf(k, grain);
      const list = byKey.get(g);
      if (list) list.push(i);
      else byKey.set(g, [i]);
    });
    const groups = [...byKey.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([key, idx]) => ({ key, idx }));
    last = { grain, groups };
    if (groups.length <= max) return last;
  }
  return last!;
}
